import { type ChangeEvent, type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Dropdown, type MenuItem as DropdownItem, useToast } from "../ui"
import {
  type Attachment,
  MAX_ATTACHMENTS,
  MAX_IMAGE_BYTES,
  MAX_TOTAL_IMAGE_BYTES,
  type PromptFile,
  composeMessage,
  extractedChars,
  formatBytes,
  imageBytes,
  imageInputAllowed,
  readAttachment,
} from "../../lib/attachments"
import type { AgentInfo, Model, ModelRef } from "../../lib/opencodeTypes"
import {
  DEFAULT_EFFORT,
  type SlashCommand,
  effortHint,
  effortId,
  effortLabel,
  effortOptions,
  findCommand,
  matchAgents,
  matchCommands,
  matchModels,
  modelKey,
  parseSlash,
} from "../../lib/slashCommands"
import {
  type AtMention,
  formatFilePath,
  insertMention,
  matchFiles,
  parseAtMention,
} from "../../lib/autocomplete"
import type { FileSystemEntry } from "../../lib/opencodeTypes"
import { clearDraft, readDraft, saveDraft } from "../../lib/draft"

// Inline rather than a dependency, matching components/brand.tsx: one path that
// takes `currentColor` so it is whatever the button around it is, in both themes.
function Paperclip({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  )
}

interface MenuItem {
  label: string
  hint?: string
  current?: boolean
  apply: () => void
}

// `items` empty with a `note` is a real state, not a bug: the menu stays open
// saying why there is nothing to pick. Closing it instead would let Enter fall
// through and send "/effort " to the model.
interface Menu {
  items: MenuItem[]
  note?: string
}

interface MessageInputProps {
  // Returns whether the send succeeded. The text and the attachments are only
  // cleared on success — silently dropping a file someone just attached because
  // the request failed is the worst thing this component could do.
  onSend: (text: string, files: PromptFile[]) => Promise<boolean>
  onSwitchModel: (ref: ModelRef) => void
  onCommand: (name: string, args: string) => void
  models: Model[] | null
  modelsError: string | null
  currentModel: ModelRef | undefined
  // The pickers live inside the composer card, the way the desktop app lays
  // them out — they are properties of the message about to be sent.
  agents: AgentInfo[] | null
  currentAgent: string | undefined
  onSwitchAgent: (name: string) => void
  commands: SlashCommand[]
  findFiles: (query: string) => Promise<FileSystemEntry[]>
  // The chat session this composer belongs to. Given one, the unsent text is
  // mirrored to storage so a remount or a reload does not swallow it.
  draftKey?: string
  disabled?: boolean
  // While a turn runs the send button becomes the stop button, the way the
  // desktop's does — one control, two moments.
  working?: boolean
  onStop: () => void
  stopping?: boolean
}

// The desktop composer's control style: quiet text chips with a chevron, no
// button chrome until hovered.
function Chip({
  label,
  open,
  toggle,
  disabled,
  title,
}: {
  label: ReactNode
  open: boolean
  toggle: () => void
  disabled?: boolean
  title: string
}) {
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      title={title}
      aria-haspopup="menu"
      aria-expanded={open}
      className="inline-flex max-w-48 items-center gap-1 rounded-md px-2 py-1 text-xs text-muted transition-colors hover:bg-surface-muted hover:text-foreground disabled:opacity-50"
    >
      <span className="truncate">{label}</span>
      <span aria-hidden className="text-[10px] text-faint">
        ⌄
      </span>
    </button>
  )
}

function StopSquare({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
    </svg>
  )
}

function ArrowUp({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  )
}

export function MessageInput({
  onSend,
  onSwitchModel,
  onCommand,
  models,
  modelsError,
  currentModel,
  agents,
  currentAgent,
  onSwitchAgent,
  commands,
  findFiles,
  draftKey,
  disabled,
  working,
  onStop,
  stopping,
}: MessageInputProps) {
  // Lazy: storage is read once, on mount. `draftKey` does not change for the
  // life of the page — one chat per session — so there is no restore effect to
  // keep in step with it.
  const [text, setText] = useState(() => (draftKey ? readDraft(draftKey) : ""))
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [active, setActive] = useState(0)
  // Escape means "this is text, not a command". It closes the menu and lets the
  // slash text be sent as an ordinary message.
  const [dismissed, setDismissed] = useState(false)
  // Drop already worked and nothing said so. An affordance that only exists once
  // you are mid-gesture is one nobody discovers.
  const [dragging, setDragging] = useState(false)
  // Decoding and re-encoding a phone photo is not instant, and a tray that stays
  // empty for a second reads as a click that did not land.
  const [reading, setReading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const toast = useToast()

  // @ mention autocomplete state
  const [atMention, setAtMention] = useState<AtMention | null>(null)
  const [atResults, setAtResults] = useState<FileSystemEntry[]>([])
  const [atLoading, setAtLoading] = useState(false)
  const [atActive, setAtActive] = useState(0)
  const [atDismissed, setAtDismissed] = useState(false)
  const atTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const slash = useMemo(() => (disabled ? undefined : parseSlash(text)), [disabled, text])
  const stage = slash ? `${slash.name}:${slash.arg === undefined ? "name" : "arg"}` : ""
  // Null rather than undefined when there is no command, so leaving the slash
  // behind and typing a new one counts as a change and lifts the dismissal.
  const slashKey = slash ? slash.name : null

  useEffect(() => setActive(0), [stage, slash?.arg])
  useEffect(() => setDismissed(false), [slashKey])

  // Debounced, or this writes to storage on every keystroke. The cleanup is
  // what makes it one: each keystroke cancels the write the last one queued.
  useEffect(() => {
    if (!draftKey) return
    const timer = setTimeout(() => saveDraft(draftKey, text), 300)
    return () => clearTimeout(timer)
  }, [draftKey, text])

  const currentInfo = useMemo(
    () =>
      models?.find(
        (model) => model.id === currentModel?.id && model.providerID === currentModel.providerID,
      ),
    [models, currentModel],
  )

  // Advisory only — see imageInputAllowed. An absent catalog and an empty
  // modality list are both "not stated" and both allow, which is what makes
  // this work over v2: it does not carry `modalities` through to
  // `capabilities`, so the list arrives empty for a model that does read
  // images.
  const imagesAllowed = imageInputAllowed(currentInfo?.capabilities?.input)

  // ── The three pickers in the controls row ──
  // Second surface onto the slash commands, same handlers, so /model and
  // /effort cannot drift from what the chips do.
  const shownModel = currentInfo ?? (currentModel ? undefined : models?.[0])
  const variant = effortId(currentModel?.variant)

  const agentItems: DropdownItem[] = (agents ?? []).map((agent) => ({
    label: (
      <span className="flex flex-col">
        <span className="capitalize">{agent.id}</span>
        {agent.description && <span className="text-xs text-faint">{agent.description}</span>}
      </span>
    ),
    selected: agent.id === currentAgent,
    onSelect: () => onSwitchAgent(agent.id),
  }))

  const modelItems: DropdownItem[] = (models ?? []).map((model) => ({
    label: model.name,
    selected: model.id === currentModel?.id && model.providerID === currentModel.providerID,
    // The variant is carried across a model change only when the new model
    // declares it — switchModel does not validate, and a bad one fails at the
    // *next* turn instead of here.
    onSelect: () => {
      const keep = effortOptions(model).includes(variant) ? variant : undefined
      onSwitchModel({
        id: model.id,
        providerID: model.providerID,
        ...(keep && keep !== DEFAULT_EFFORT ? { variant: keep } : {}),
      })
    },
  }))

  // A fresh session has no model ref until the first message, but the model it
  // *will* use is already on screen — effort targets that one rather than
  // sitting disabled until something else happens first.
  const effortTarget = currentModel ?? (shownModel ? { id: shownModel.id, providerID: shownModel.providerID } : undefined)
  const effortItems: DropdownItem[] = effortTarget
    ? effortOptions(currentInfo ?? shownModel).map((id) => ({
        label: (
          <span className="flex flex-col">
            <span>{effortLabel(id)}</span>
            {effortHint(id) && <span className="text-xs text-faint">{effortHint(id)}</span>}
          </span>
        ),
        selected: (variant ?? DEFAULT_EFFORT) === id,
        onSelect: () =>
          onSwitchModel({
            id: effortTarget.id,
            providerID: effortTarget.providerID,
            // The gateway's implicit effort is Medium; no fourth variant exists.
            ...(id === DEFAULT_EFFORT ? {} : { variant: id }),
          }),
      }))
    : []

  // Slash command menu. Built-in commands (model, effort) are handled here;
  // custom commands and session commands (compact, undo, etc.) are dispatched
  // via onCommand.
  const menu = useMemo<Menu | undefined>(() => {
    if (!slash || dismissed) return undefined

    const applying = (ref: ModelRef) => () => {
      onSwitchModel(ref)
      setText("")
    }

    if (slash.arg === undefined) {
      const matches = matchCommands(slash.name, commands)
      if (matches.length === 0) return undefined
      return {
        items: matches.map((command) => ({
          label: `/${command.name}`,
          hint: command.description,
          // A command with a picker moves on to the argument stage — the
          // trailing space is what moves the parse on; there is no second piece
          // of state doing it. One without a picker has nothing to choose, so
          // choosing it *is* running it.
          apply: command.picker
            ? () => setText(`/${command.name} `)
            : () => {
                setText("")
                onCommand(command.name, "")
              },
        })),
      }
    }

    const command = findCommand(slash.name, commands)
    if (!command) return undefined
    // No picker, no menu: Enter reaches submit(), which dispatches it. This
    // used to test `name === "model"` and treat everything else as /effort, so
    // /compact, /undo, /redo, /share, /unshare and /export were unreachable —
    // the menu offered reasoning depths for them, and submit() refuses to send
    // while a menu is open. Picking /compact changed the reasoning depth.
    if (!command.picker) return undefined

    if (command.picker === "agent") {
      const matches = matchAgents(agents ?? [], slash.arg)
      if (matches.length === 0) return { items: [], note: agents ? "No agent matches" : "Loading agents…" }
      return {
        items: matches.map((agent) => ({
          label: agent.id,
          hint: agent.description,
          current: agent.id === currentAgent,
          apply: () => {
            setText("")
            onSwitchAgent(agent.id)
          },
        })),
      }
    }

    if (!models) {
      return { items: [], note: modelsError ? `Could not load models: ${modelsError}` : "Loading models…" }
    }

    if (command.picker === "model") {
      const matches = matchModels(models, slash.arg)
      if (matches.length === 0) return { items: [], note: "No model matches" }
      return {
        items: matches.map((model) => ({
          label: model.name,
          hint: modelKey(model),
          current: model.id === currentModel?.id && model.providerID === currentModel.providerID,
          // The variant is deliberately dropped. It may not exist on the model
          // being moved to, and the agent accepts a bad one here and only fails
          // at the next turn.
          apply: applying({ id: model.id, providerID: model.providerID }),
        })),
      }
    }

    if (!currentModel) {
      return { items: [], note: "No model selected yet — choose one with /model" }
    }

    const ids = effortOptions(currentInfo)
    if (ids.length <= 1) {
      return {
        items: [],
        note: `${currentInfo?.name ?? currentModel.id} offers one effort level`,
      }
    }
    return {
      items: ids.map((id) => ({
        label: effortLabel(id),
        hint: effortHint(id),
        current: effortId(currentModel.variant) === id,
        // Medium is also the gateway's implicit effort, so its ref can omit
        // the variant when retaining an older model catalog.
        apply: applying({
          id: currentModel.id,
          providerID: currentModel.providerID,
          ...(id === DEFAULT_EFFORT ? {} : { variant: id }),
        }),
      })),
    }
  }, [
    slash,
    dismissed,
    models,
    modelsError,
    currentModel,
    currentInfo,
    onSwitchModel,
    commands,
    agents,
    currentAgent,
    onSwitchAgent,
    onCommand,
  ])

  // @ mention autocomplete: debounced file search
  useEffect(() => {
    if (!atMention || atDismissed) {
      setAtResults([])
      return
    }

    clearTimeout(atTimerRef.current)
    if (!atMention.query) {
      setAtResults([])
      setAtLoading(false)
      return
    }

    atTimerRef.current = setTimeout(async () => {
      setAtLoading(true)
      try {
        const results = await findFiles(atMention.query)
        setAtResults(matchFiles(results, atMention.query))
      } catch {
        setAtResults([])
      } finally {
        setAtLoading(false)
      }
    }, 200)

    return () => clearTimeout(atTimerRef.current)
  }, [atMention, atDismissed, findFiles])

  useEffect(() => setAtActive(0), [atMention?.query])
  useEffect(() => setAtDismissed(false), [atMention?.query])

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return
    setReading(true)
    try {
      await readInto(files)
    } finally {
      setReading(false)
    }
  }

  const readInto = async (files: File[]) => {
    const problems: string[] = []
    // Accumulated rather than checked against the current state, so the total
    // and the count are right across a multi-file pick.
    let next = attachments

    for (const file of files) {
      if (next.length >= MAX_ATTACHMENTS) {
        problems.push(`At most ${MAX_ATTACHMENTS} files per message.`)
        break
      }
      try {
        // The character budget is passed in, not checked afterwards, so a file
        // that cannot fit is refused by the reader with the real count in the
        // message rather than after the work of extracting it.
        const attachment = await readAttachment(file, imagesAllowed, extractedChars(next))
        if (next.some((existing) => existing.id === attachment.id)) {
          problems.push(`${attachment.name} is already attached.`)
          continue
        }
        // Images carry a byte budget of their own: their bytes are what actually
        // travels, whereas a Word file's bytes never leave the browser.
        if (attachment.kind === "image" && imageBytes(next) + attachment.size > MAX_TOTAL_IMAGE_BYTES) {
          problems.push(
            `${attachment.name} would take the images over ${formatBytes(MAX_TOTAL_IMAGE_BYTES)}.`,
          )
          continue
        }
        next = [...next, attachment]
      } catch (error) {
        problems.push(error instanceof Error ? error.message : `Could not read ${file.name}.`)
      }
    }

    setAttachments(next)
    // One toast per problem rather than a joined string: each names a different
    // file, and a wall of concatenated sentences is not read.
    for (const problem of problems) toast(problem, "error")
  }

  const handleFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    // Cleared or picking the same file again fires no change event at all.
    e.target.value = ""
    await addFiles(files)
  }

  const handleDragOver = (e: DragEvent<HTMLFormElement>) => {
    if (disabled) return
    // Files only. Dragging selected text within the page also fires these, and
    // lighting the composer up for that is a lie about what will happen.
    if (!e.dataTransfer.types.includes("Files")) return
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLFormElement>) => {
    // Fires when crossing into a child too, so only a pointer that actually left
    // the form counts.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
    setDragging(false)
  }

  const handleDrop = async (e: DragEvent<HTMLFormElement>) => {
    setDragging(false)
    if (disabled) return
    e.preventDefault()
    await addFiles(Array.from(e.dataTransfer.files))
  }

  const canSend = !disabled && (text.trim() !== "" || attachments.length > 0)

  const submit = async () => {
    if (disabled) return

    if (menu) {
      const message =
        menu.items.length > 0
          ? "Pick an option, or press Escape to send this as a message."
          : menu.note
      if (message) toast(message, "error")
      return
    }
    // A command we do not implement is never handed to the model as prose —
    // "/compact" would read as an instruction rather than the no-op it is here.
    // Unless Escape was pressed, which is how someone says they meant it as text.
    if (!dismissed && slash && slash.name !== "" && !findCommand(slash.name, commands)) {
      toast(`Unknown command: /${slash.name}. Press Escape to send it as a message.`, "error")
      return
    }
    // Custom commands (from agent config) are dispatched via onCommand rather
    // than sent as text — the agent resolves the template server-side.
    if (!dismissed && slash && slash.name !== "") {
      const cmd = findCommand(slash.name, commands)
      if (cmd?.isCustom) {
        onCommand(slash.name, slash.arg ?? "")
        setText("")
        if (draftKey) clearDraft(draftKey)
        return
      }
    }
    if (text.trim() === "" && attachments.length === 0) return

    const message = composeMessage(text, attachments)
    if (!(await onSend(message.text, message.files))) return
    setText("")
    setAttachments([])
    // Immediately, not on the debounce: a reload in the next 300ms must not
    // resurrect a message that has already been sent.
    if (draftKey) clearDraft(draftKey)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    void submit()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // @ mention autocomplete takes priority when active
    if (atMention && !atDismissed) {
      if (e.key === "Escape") {
        e.preventDefault()
        setAtDismissed(true)
        return
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        if (atResults.length === 0) return
        const step = e.key === "ArrowDown" ? 1 : -1
        setAtActive((index) => (index + step + atResults.length) % atResults.length)
        return
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault()
        if (atResults.length > 0) {
          const selected = atResults[atActive]
          if (selected) {
            const result = insertMention(text, atMention, selected.path)
            setText(result.text)
            setAtMention(null)
            setAtResults([])
            // Move cursor to after the inserted mention
            requestAnimationFrame(() => {
              if (textareaRef.current) {
                textareaRef.current.selectionStart = result.cursorPos
                textareaRef.current.selectionEnd = result.cursorPos
              }
            })
          }
        }
        return
      }
    }

    // Slash command menu
    if (menu) {
      if (e.key === "Escape") {
        e.preventDefault()
        setDismissed(true)
        return
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        if (menu.items.length === 0) return
        const step = e.key === "ArrowDown" ? 1 : -1
        setActive((index) => (index + step + menu.items.length) % menu.items.length)
        return
      }
      if (e.key === "Enter" || e.key === "Tab") {
        // Swallowed even with nothing selectable, so Enter cannot fall through
        // and send the half-typed command.
        e.preventDefault()
        menu.items[active]?.apply()
        return
      }
    }

    if (e.key === "Enter" && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
      e.preventDefault()
      void submit()
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value
    setText(newText)

    // Check for @ mention trigger
    const cursorPos = e.target.selectionStart
    const mention = parseAtMention(newText, cursorPos)
    if (mention) {
      setAtMention(mention)
      setAtDismissed(false)
    } else {
      setAtMention(null)
      setAtResults([])
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="relative mx-auto w-full max-w-3xl px-4 pb-4 pt-1"
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-accent bg-surface/90 text-sm font-medium text-accent">
          <Paperclip className="mr-2 size-4" />
          Drop to attach
        </div>
      )}
      {(attachments.length > 0 || reading) && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {attachments.map((attachment) => (
            <span
              key={attachment.id}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-surface-muted py-1 pl-1 pr-2 text-xs"
            >
              {attachment.kind === "image" ? (
                <img
                  src={attachment.dataUrl}
                  alt={attachment.name}
                  className="size-8 rounded object-cover"
                />
              ) : (
                <Paperclip className="ml-1 size-3.5 text-muted" />
              )}
              <span className="max-w-40 truncate font-medium text-foreground">{attachment.name}</span>
              <span className="text-muted">
                {attachment.kind === "image"
                  ? formatBytes(attachment.size)
                  : `${Math.round(attachment.chars / 1000)}k chars`}
              </span>
              <button
                type="button"
                aria-label={`Remove ${attachment.name}`}
                onClick={() => setAttachments((list) => list.filter((item) => item.id !== attachment.id))}
                className="text-muted hover:text-foreground"
              >
                ×
              </button>
            </span>
          ))}
          {reading && <span className="text-xs text-muted">Reading…</span>}
        </div>
      )}

      <div className="relative rounded-xl border border-border bg-surface shadow-sm focus-within:border-border-strong">
        {/* Slash command menu */}
        {menu && (
          <div
            id="slash-menu"
            role="listbox"
            className="absolute bottom-full left-0 right-0 z-20 mb-1 max-h-56 overflow-y-auto rounded-md border border-border bg-surface shadow-lg"
          >
            {menu.items.map((item, index) => (
              <button
                key={item.label}
                id={`slash-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === active}
                // Without this the textarea blurs before the click lands, and
                // the menu is gone by the time the handler would have run.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={item.apply}
                className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm ${
                  index === active ? "bg-surface-muted" : ""
                }`}
              >
                <span className="text-foreground">
                  {item.current && <span className="mr-1 text-accent">•</span>}
                  {item.label}
                </span>
                {item.hint && <span className="text-xs text-muted">{item.hint}</span>}
              </button>
            ))}
            {menu.items.length === 0 && menu.note && (
              <div className="px-3 py-2 text-sm text-muted">{menu.note}</div>
            )}
          </div>
        )}

        {/* @ file mention autocomplete */}
        {atMention && !atDismissed && (
          <div
            id="at-menu"
            role="listbox"
            className="absolute bottom-full left-0 right-0 z-20 mb-1 max-h-56 overflow-y-auto rounded-md border border-border bg-surface shadow-lg"
          >
            {atLoading && (
              <div className="px-3 py-2 text-sm text-muted">Searching files…</div>
            )}
            {!atLoading && atResults.length === 0 && atMention.query && (
              <div className="px-3 py-2 text-sm text-muted">No files match</div>
            )}
            {!atLoading && atResults.map((entry, index) => (
              <button
                key={entry.path}
                id={`at-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === atActive}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setAtActive(index)}
                onClick={() => {
                  const result = insertMention(text, atMention, entry.path)
                  setText(result.text)
                  setAtMention(null)
                  setAtResults([])
                  requestAnimationFrame(() => {
                    if (textareaRef.current) {
                      textareaRef.current.selectionStart = result.cursorPos
                      textareaRef.current.selectionEnd = result.cursorPos
                    }
                  })
                }}
                className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm ${
                  index === atActive ? "bg-surface-muted" : ""
                }`}
              >
                <span className="font-mono text-foreground truncate">
                  {entry.type === "directory" ? "📁 " : ""}{formatFilePath(entry.path)}
                </span>
                <span className="text-xs text-muted">{entry.type}</span>
              </button>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything — / for commands, @ for files"
          disabled={disabled}
          rows={2}
          role="combobox"
          aria-expanded={menu !== undefined || (atMention !== null && !atDismissed)}
          aria-controls={menu ? "slash-menu" : atMention && !atDismissed ? "at-menu" : undefined}
          aria-activedescendant={
            menu && menu.items.length > 0
              ? `slash-option-${active}`
              : atMention && !atDismissed && atResults.length > 0
                ? `at-option-${atActive}`
                : undefined
          }
          className="block w-full resize-none border-0 bg-transparent px-3 pb-1 pt-3 text-sm outline-none placeholder:text-faint disabled:opacity-50"
        />

        <div className="flex items-center gap-0.5 px-2 pb-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => fileRef.current?.click()}
            title={`Attach code, text, Office files, spreadsheets, PDFs or images. Images are reduced to ${formatBytes(MAX_IMAGE_BYTES)} when needed. You can also drop files here.`}
            aria-label="Attach files"
            className="inline-flex size-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-foreground disabled:opacity-50"
          >
            <Paperclip className="size-4" />
          </button>

          {agentItems.length > 0 && (
            <Dropdown
              drop="up"
              items={agentItems}
              trigger={({ open, toggle }) => (
                <Chip
                  open={open}
                  toggle={toggle}
                  disabled={disabled || agentItems.length <= 1}
                  label={
                    <span className="capitalize">{currentAgent ?? "Agent"}</span>
                  }
                  title={
                    agentItems.length <= 1
                      ? "This container declares a single agent"
                      : "The agent mode — Build acts on the code, Plan only reads and proposes"
                  }
                />
              )}
            />
          )}

          <Dropdown
            drop="up"
            items={modelItems}
            trigger={({ open, toggle }) => (
              <Chip
                open={open}
                toggle={toggle}
                disabled={disabled || !models?.length}
                label={shownModel?.name ?? currentModel?.id ?? (modelsError ? "Models unavailable" : "Loading…")}
                title={modelsError ?? "The model this session talks to"}
              />
            )}
          />

          {effortItems.length > 1 ? (
            <Dropdown
              drop="up"
              items={effortItems}
              trigger={({ open, toggle }) => (
                <Chip
                  open={open}
                  toggle={toggle}
                  disabled={disabled}
                  label={effortLabel(variant)}
                  title="How much reasoning the model spends"
                />
              )}
            />
          ) : (
            // Clickable even with nothing to offer: a chip that swallows the
            // click reads as broken. The click says why, and what to do.
            <Chip
              open={false}
              toggle={() =>
                toast(
                  models?.length
                    ? "This model declares no effort levels. Sessions created before the latest workspace image keep the old catalog - create a new session to get Default / Medium / High."
                    : "Effort levels appear once the model list has loaded.",
                )
              }
              disabled={disabled}
              label={effortLabel(variant)}
              title="How much reasoning the model spends"
            />
          )}

          <div className="ml-auto" />

          {working ? (
            <button
              type="button"
              onClick={onStop}
              disabled={stopping}
              aria-label="Stop"
              title="Interrupt the running turn. What was already produced stays in the transcript."
              className="inline-flex size-7 items-center justify-center rounded-lg border border-border bg-surface-muted text-muted transition-colors hover:border-danger/50 hover:text-danger disabled:opacity-50"
            >
              <StopSquare className="size-3.5" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send"
              title="Send — Enter. Shift+Enter for a new line."
              className="inline-flex size-7 items-center justify-center rounded-lg border border-border bg-surface-muted text-muted transition-colors hover:text-foreground disabled:opacity-40"
            >
              <ArrowUp className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {/*
          No accept filter. The picker's filter is enforced by the OS, which
          resolves each entry to a type it recognises and silently drops the
          ones it cannot — so a curated list greys out exactly the files this
          product sees most often (.abap, .impex, a .log, anything with an
          unregistered extension) without saying why. readAttachment already
          decides by content and answers every wrong file with a sentence;
          a greyed-out row answers with nothing.
      */}
      <input ref={fileRef} type="file" multiple onChange={handleFiles} className="hidden" />
    </form>
  )
}
